import { useState } from "react";
import { Link } from "react-router-dom";

import { DataTable } from "../components/DataTable";
import { Card, ErrorBox, Loading, PageHeader } from "../components/Page";
import { StatTile } from "../components/StatTile";
import { day, num } from "../lib/format";
import { useApi } from "../lib/useApi";

interface Milestone { name: string; stage: string | null; at: string | null; detail: string }
interface Container { no: string | null; size: string | null; status: string | null; at: string | null; description: string | null }
interface Shipment {
  carrier: string; job_no: string; service: string | null; status: string | null; stage: string; stage_on: string | null;
  state: "open" | "done" | "dropped"; on_water: boolean; direction: string; direction_label: string;
  shipper: string | null; consignee: string | null; origin: string | null; destination: string | null;
  vessel: string | null; voyage: string | null; booked_on: string | null; sails_on: string | null; sailed_on: string | null;
  arrived_on: string | null; released_on: string | null; cargo: string | null; packages: string | null; weight_lbs: number | null;
  containers: Container[]; milestones: Milestone[];
}
interface View {
  carrier?: string; as_of: string | null; fetched_at: string | null; shipments: Shipment[];
  totals: { jobs: number; open: number; on_water: number; dropped: number; by_direction: Record<string, number> } | null;
}

const STATES: Record<string, string> = { open: "On the way", done: "Landed", dropped: "Booked, never shipped", "": "Every job" };

/** "PORT OF PALM BEACH SEAPORT" -> "Palm Beach"; "BASSETERRE SEAPORT (ST KITTS)" -> "Basseterre (St Kitts)". */
function port(name: string | null): string {
  if (!name) return "";
  const s = name.replace(/^PORT OF /, "").replace(/ SEAPORT\b/, "").toLowerCase();
  return s.replace(/\b[a-z]/g, (c) => c.toUpperCase());
}
const party = (name: string | null) => (name ?? "").toLowerCase().replace(/\b[a-z]/g, (c) => c.toUpperCase());

export function Shipments() {
  const { data: d, error, reload } = useApi<View>("/shipments");
  const [state, setState] = useState("open");
  const [picked, setPicked] = useState<string | null>(null);
  if (error) return <ErrorBox error={error} onRetry={reload} />;
  if (!d) return <Loading />;
  if (!d.totals) {
    return (
      <>
        <PageHeader title="Shipments" sub="What is on the water with Tropical Shipping." />
        <Card><p className="note muted">Nothing from Tropical yet. It is pulled every morning; an admin can pull it now from{" "}
          <Link to="/sources/TROPICAL_SHIPMENTS">Integrations</Link>.</p></Card>
      </>
    );
  }
  const t = d.totals;
  const rows = state ? d.shipments.filter((s) => s.state === state) : d.shipments;
  const job = d.shipments.find((s) => s.job_no === picked);

  return (
    <>
      <PageHeader title="Shipments"
        sub={<>Every job with Tropical Shipping for the last year, from their eServices{d.as_of ? <> as of {day(d.as_of)}</> : null}.
          A shipment never changes stock here; the warehouses' own reports do that when it lands.</>} />

      <section className="tiles" aria-label="Headline figures">
        <StatTile label="On the way" value={num(t.open)} sub="booked, at the dock or sailing" />
        <StatTile label="On the water" value={num(t.on_water)} sub="sailed, not yet arrived" />
        <StatTile label="To St. Kitts" value={num(t.by_direction.to_st_kitts)} sub="materials, on the way" />
        <StatTile label="To customers and the US" value={num((t.by_direction.to_customer ?? 0) + (t.by_direction.to_us ?? 0))}
          sub="rum, on the way" />
      </section>

      {job && (
        <Card title={`Job ${job.job_no}`} actions={<button className="btn" onClick={() => setPicked(null)}>Close</button>}>
          <p className="note">
            {party(job.shipper)} → {party(job.consignee)}, {port(job.origin)} to {port(job.destination)}
            {job.vessel ? <>, on {job.vessel} {job.voyage}</> : null}. {job.cargo}{job.packages ? <> ({job.packages}
            {job.weight_lbs ? <>, {num(job.weight_lbs)} lb</> : null})</> : null}.
          </p>
          <table className="grid compact" data-testid="shipment-milestones">
            <thead><tr><th>Milestone</th><th>When</th><th>Detail</th></tr></thead>
            <tbody>
              {job.milestones.map((m, i) => (
                <tr key={i} className={m.at ? "" : "muted"}>
                  <td className="name">{m.name}</td><td>{m.at ? day(m.at) : "not yet"}</td>
                  <td>{m.at ? m.detail.replace(/^[^·]*·\s*·?\s*/, "") : ""}</td>
                </tr>
              ))}
            </tbody>
          </table>
          {job.containers.some((c) => c.no) && (
            <p className="note muted">
              {job.containers.filter((c) => c.no).map((c) => `${c.no}${c.size ? ` (${c.size.toLowerCase()})` : ""}${c.status ? `: ${c.status.toLowerCase()}${c.at ? ` ${day(c.at)}` : ""}` : ""}`).join("; ")}
            </p>
          )}
        </Card>
      )}

      <Card title="Jobs" actions={
        <select aria-label="Show" value={state} onChange={(e) => setState(e.target.value)}>
          {Object.entries(STATES).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
        </select>}>
        <DataTable<Shipment> rows={rows} rowKey={(s) => s.job_no} testId="shipments" filterPlaceholder="Filter by job, port or cargo…"
          empty="No jobs in this view."
          columns={[
            { key: "job_no", label: "Job", render: (s) => (
              <button className="link-btn" onClick={() => setPicked(s.job_no)} aria-label={`Job ${s.job_no} milestones`}>{s.job_no}</button>) },
            { key: "direction_label", label: "Going" },
            { key: "route", label: "Route", value: (s) => `${port(s.origin)} ${port(s.destination)}`,
              render: (s) => <>{port(s.origin)} → {port(s.destination)}</> },
            { key: "consignee", label: "To", render: (s) => party(s.consignee) },
            { key: "cargo", label: "Cargo", render: (s) => <>{s.cargo}{s.packages ? <span className="muted"> · {s.packages.toLowerCase()}</span> : null}</> },
            { key: "stage", label: "Where it is", value: (s) => s.stage,
              render: (s) => <>{s.stage}{s.stage_on ? <span className="muted"> {day(s.stage_on)}</span> : null}</> },
            { key: "sails_on", label: "Sails", value: (s) => s.sailed_on ?? s.sails_on,
              render: (s) => (s.sailed_on ? day(s.sailed_on) : s.sails_on ? <span className="muted">due {day(s.sails_on)}</span> : "") },
            { key: "booked_on", label: "Booked", render: (s) => day(s.booked_on) },
          ]} />
        {t.dropped > 0 && state === "open" && (
          <p className="note muted">{t.dropped} bookings never reached Tropical's dock in 60 days; they are under "Booked, never shipped".</p>
        )}
      </Card>
    </>
  );
}
